import React, { useState, useEffect } from 'react';
import { MorningRoutine, RoutineStep, RoutineExecutionState, SmartDevice } from './interfaces/types';
import { getDeviceControlInterface } from './DeviceControlHub';

interface MorningRoutineOrchestratorProps {
  routines: MorningRoutine[];
  devices: SmartDevice[];
  onRoutineStart: (routineId: string) => void;
  onRoutineComplete: (routineId: string, success: boolean) => void;
  onStepComplete: (routineId: string, stepId: string, success: boolean) => void;
  paused: boolean;
  enabled: boolean;
}

const MorningRoutineOrchestrator: React.FC<MorningRoutineOrchestratorProps> = ({
  routines,
  devices,
  onRoutineStart,
  onRoutineComplete,
  onStepComplete,
  paused,
  enabled
}) => {
  const [activeRoutines, setActiveRoutines] = useState<RoutineExecutionState[]>([]);
  const [executionLog, setExecutionLog] = useState<{
    timestamp: Date;
    routineId: string;
    message: string;
    type: 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS';
  }[]>([]);
  
  // Add a message to the execution log
  const logMessage = (routineId: string, message: string, type: 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS') => {
    setExecutionLog(prev => [
      ...prev,
      {
        timestamp: new Date(),
        routineId,
        message,
        type
      }
    ].slice(-50)); // Keep only the last 50 log entries
  };
  
  // Check if a routine should start based on time and day
  useEffect(() => {
    if (!enabled || paused) return;
    
    // Check every minute if we need to start a routine
    const checkInterval = setInterval(() => {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentDay = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][now.getDay()];
      
      // Find routines that should start now
      routines.forEach(routine => {
        if (!routine.enabled) return;
        
        // Skip if routine is already active
        if (activeRoutines.some(r => r.routineId === routine.id)) return;
        
        // Check if today is a valid day for this routine
        if (!routine.daysOfWeek.includes(currentDay as any)) return;
        
        // Check if it's the start time for this routine
        if (routine.defaultStartTime.hour === currentHour && 
            routine.defaultStartTime.minute === currentMinute) {
          // Start the routine
          startRoutine(routine);
        }
      });
    }, 30000); // Check every 30 seconds
    
    return () => {
      clearInterval(checkInterval);
    };
  }, [enabled, paused, routines, activeRoutines]);
  
  // Process active routines
  useEffect(() => {
    if (!enabled || paused || activeRoutines.length === 0) return;
    
    // Process each active routine
    const processInterval = setInterval(() => {
      setActiveRoutines(prevRoutines => {
        return prevRoutines.map(routineState => {
          // Skip routines that are paused, completed, or failed
          if (routineState.status !== 'IN_PROGRESS') return routineState;
          
          // Get the routine configuration
          const routine = routines.find(r => r.id === routineState.routineId);
          if (!routine) {
            // Routine not found, mark as failed
            logMessage(routineState.routineId, 'Routine configuration not found', 'ERROR');
            return {
              ...routineState,
              status: 'FAILED',
              userFeedback: 'Routine configuration not found'
            };
          }
          
          // Get the current step
          const currentStep = routine.steps.find(s => s.id === routineState.currentStepId);
          if (!currentStep) {
            // No current step or all steps completed
            const isComplete = routine.steps.every(step => 
              routineState.completedStepIds.includes(step.id) || 
              routineState.skippedStepIds.includes(step.id) ||
              step.isOptional
            );
            
            if (isComplete) {
              // All steps completed
              logMessage(routineState.routineId, `Routine "${routine.name}" completed successfully`, 'SUCCESS');
              onRoutineComplete(routineState.routineId, true);
              return {
                ...routineState,
                status: 'COMPLETED',
                userFeedback: 'Routine completed successfully'
              };
            } else {
              // Can't find the next step
              logMessage(routineState.routineId, `Could not determine next step for routine "${routine.name}"`, 'ERROR');
              onRoutineComplete(routineState.routineId, false);
              return {
                ...routineState,
                status: 'FAILED',
                userFeedback: 'Could not determine next step'
              };
            }
          }
          
          // Check if step should be executed
          return checkAndExecuteStep(routineState, routine, currentStep);
        });
      });
    }, 1000); // Process routines every second
    
    return () => {
      clearInterval(processInterval);
    };
  }, [enabled, paused, activeRoutines, routines, onRoutineComplete, onStepComplete]);
  
  // Start a new routine
  const startRoutine = (routine: MorningRoutine) => {
    // Find the first step
    const firstStep = routine.steps[0];
    if (!firstStep) {
      logMessage(routine.id, `Cannot start routine "${routine.name}" - no steps defined`, 'ERROR');
      return;
    }
    
    // Create a new routine execution state
    const newRoutineState: RoutineExecutionState = {
      routineId: routine.id,
      startTime: new Date(),
      currentStepId: firstStep.id,
      completedStepIds: [],
      skippedStepIds: [],
      failedStepIds: [],
      status: 'IN_PROGRESS'
    };
    
    // Add to active routines
    setActiveRoutines(prev => [...prev, newRoutineState]);
    
    // Log start
    logMessage(routine.id, `Started routine "${routine.name}"`, 'INFO');
    
    // Notify parent
    onRoutineStart(routine.id);
  };
  
  // Check if a step's condition is met and execute its actions
  const checkAndExecuteStep = (
    routineState: RoutineExecutionState, 
    routine: MorningRoutine, 
    currentStep: RoutineStep
  ): RoutineExecutionState => {
    // Check if the step condition is met
    const conditionMet = checkStepCondition(currentStep);
    
    if (conditionMet) {
      // Execute step actions
      executeStepActions(currentStep, routine.id)
        .then(success => {
          // Update routine state after actions complete
          setActiveRoutines(prevRoutines => 
            prevRoutines.map(rs => {
              if (rs.routineId !== routineState.routineId) return rs;
              
              if (success) {
                // Mark step as completed
                logMessage(routine.id, `Completed step "${currentStep.name}"`, 'SUCCESS');
                onStepComplete(routine.id, currentStep.id, true);
                
                // Find the next step
                const nextStep = determineNextStep(routine, currentStep, rs);
                
                if (nextStep) {
                  // Move to next step
                  return {
                    ...rs,
                    currentStepId: nextStep.id,
                    completedStepIds: [...rs.completedStepIds, currentStep.id]
                  };
                } else {
                  // No more steps
                  logMessage(routine.id, `Routine "${routine.name}" completed successfully`, 'SUCCESS');
                  onRoutineComplete(routine.id, true);
                  return {
                    ...rs,
                    completedStepIds: [...rs.completedStepIds, currentStep.id],
                    status: 'COMPLETED',
                    userFeedback: 'Routine completed successfully'
                  };
                }
              } else {
                // Step failed
                if (currentStep.isOptional) {
                  // Skip optional step
                  logMessage(routine.id, `Skipped optional step "${currentStep.name}"`, 'WARNING');
                  
                  // Find the next step
                  const nextStep = determineNextStep(routine, currentStep, rs);
                  
                  if (nextStep) {
                    // Move to next step
                    return {
                      ...rs,
                      currentStepId: nextStep.id,
                      skippedStepIds: [...rs.skippedStepIds, currentStep.id]
                    };
                  } else {
                    // No more steps
                    logMessage(routine.id, `Routine "${routine.name}" completed with skipped steps`, 'SUCCESS');
                    onRoutineComplete(routine.id, true);
                    return {
                      ...rs,
                      skippedStepIds: [...rs.skippedStepIds, currentStep.id],
                      status: 'COMPLETED',
                      userFeedback: 'Routine completed with skipped steps'
                    };
                  }
                } else {
                  // Required step failed
                  logMessage(routine.id, `Required step "${currentStep.name}" failed`, 'ERROR');
                  onStepComplete(routine.id, currentStep.id, false);
                  onRoutineComplete(routine.id, false);
                  return {
                    ...rs,
                    failedStepIds: [...rs.failedStepIds, currentStep.id],
                    status: 'FAILED',
                    userFeedback: `Step "${currentStep.name}" failed`
                  };
                }
              }
            })
          );
        });
    }
    
    // Return the current state while the actions are being executed
    return routineState;
  };
  
  // Check if a step's condition is met
  const checkStepCondition = (step: RoutineStep): boolean => {
    const now = new Date();
    
    switch (step.condition.type) {
      case 'TIME':
        if (step.condition.timeWindow) {
          const startTime = new Date();
          startTime.setHours(step.condition.timeWindow.start.hour);
          startTime.setMinutes(step.condition.timeWindow.start.minute);
          startTime.setSeconds(0);
          
          const endTime = new Date();
          endTime.setHours(step.condition.timeWindow.end.hour);
          endTime.setMinutes(step.condition.timeWindow.end.minute);
          endTime.setSeconds(0);
          
          return now >= startTime && now <= endTime;
        }
        return true; // No time window specified, always true
        
      case 'BED_EXIT':
        // In a real app, we'd check bed sensors
        // For the demo, we'll return true during morning hours (6-9 AM)
        return now.getHours() >= 6 && now.getHours() <= 9;
        
      case 'MOTION':
        // In a real app, we'd check motion sensors
        // For the demo, we'll return true with 80% probability during morning hours
        return (now.getHours() >= 6 && now.getHours() <= 9) && Math.random() > 0.2;
        
      case 'PRESENCE':
        // In a real app, we'd check presence sensors
        // For the demo, we'll return true with 90% probability during morning hours
        return (now.getHours() >= 6 && now.getHours() <= 9) && Math.random() > 0.1;
        
      case 'DEVICE_STATE':
        // In a real app, we'd check the actual device state
        if (step.condition.deviceId) {
          const deviceInterface = getDeviceControlInterface();
          if (deviceInterface) {
            // Async check, but we'll return true for the demo
            return true;
          }
        }
        return false;
        
      case 'WEATHER':
        // In a real app, we'd check weather API
        // For the demo, we'll return true
        return true;
        
      default:
        return true;
    }
  };
  
  // Execute a step's actions
  const executeStepActions = async (step: RoutineStep, routineId: string): Promise<boolean> => {
    try {
      const deviceInterface = getDeviceControlInterface();
      if (!deviceInterface) {
        logMessage(routineId, `Device control interface not available for step "${step.name}"`, 'ERROR');
        return false;
      }
      
      // Execute each action sequentially
      for (const action of step.actions) {
        // Apply delay if specified
        if (action.delayBefore) {
          await new Promise(resolve => setTimeout(resolve, action.delayBefore));
        }
        
        // Execute the command
        logMessage(routineId, `Executing "${action.command.commandType}" on device ${action.deviceId}`, 'INFO');
        const result = await deviceInterface.executeCommand(action.deviceId, action.command);
        
        if (!result.success) {
          // Command failed, try fallback if available
          if (action.fallbackAction) {
            logMessage(routineId, `Primary action failed, attempting fallback for step "${step.name}"`, 'WARNING');
            const fallbackResult = await deviceInterface.executeCommand(
              action.fallbackAction.deviceId, 
              action.fallbackAction.command
            );
            
            if (!fallbackResult.success) {
              logMessage(routineId, `Fallback action failed for step "${step.name}"`, 'ERROR');
              return false;
            }
          } else {
            logMessage(routineId, `Action failed for step "${step.name}" and no fallback available`, 'ERROR');
            return false;
          }
        }
      }
      
      return true;
    } catch (error) {
      logMessage(routineId, `Error executing actions for step "${step.name}": ${error}`, 'ERROR');
      return false;
    }
  };
  
  // Determine the next step in a routine
  const determineNextStep = (
    routine: MorningRoutine, 
    currentStep: RoutineStep, 
    routineState: RoutineExecutionState
  ): RoutineStep | null => {
    // If the current step specifies a next step, use that
    if (currentStep.nextStepId) {
      return routine.steps.find(s => s.id === currentStep.nextStepId) || null;
    }
    
    // Otherwise, find the next step in the sequence
    const currentIndex = routine.steps.findIndex(s => s.id === currentStep.id);
    if (currentIndex >= 0 && currentIndex < routine.steps.length - 1) {
      return routine.steps[currentIndex + 1];
    }
    
    // No more steps
    return null;
  };
  
  // Render component
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-medium mb-3">Morning Routine Orchestrator</h3>
      
      <div className="mb-4">
        <div className={`px-3 py-1 rounded-full text-sm font-medium inline-block
          ${!enabled ? 'bg-gray-100 text-gray-800' :
          paused ? 'bg-yellow-100 text-yellow-800' : 
          'bg-green-100 text-green-800'}`}>
          {!enabled ? 'Disabled' : paused ? 'Paused' : 'Active'}
        </div>
      </div>
      
      {/* Active Routines */}
      {activeRoutines.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium mb-2">Active Routines</h4>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {activeRoutines.map(routineState => {
              const routine = routines.find(r => r.id === routineState.routineId);
              if (!routine) return null;
              
              const currentStep = routine.steps.find(s => s.id === routineState.currentStepId);
              
              return (
                <div key={routineState.routineId} className="border rounded-lg p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium">{routine.name}</div>
                      <div className="text-xs text-gray-500">
                        Started: {routineState.startTime.toLocaleTimeString()}
                      </div>
                    </div>
                    <div className={`px-2 py-0.5 rounded-full text-xs font-medium
                      ${routineState.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                        routineState.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                        routineState.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'}`}>
                      {routineState.status}
                    </div>
                  </div>
                  
                  {currentStep && routineState.status === 'IN_PROGRESS' && (
                    <div className="mb-2">
                      <div className="text-xs text-gray-500">Current Step:</div>
                      <div className="text-sm">{currentStep.name}</div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-3 gap-1 text-xs">
                    <div>
                      <span className="text-green-600 font-medium">
                        {routineState.completedStepIds.length}
                      </span> completed
                    </div>
                    <div>
                      <span className="text-yellow-600 font-medium">
                        {routineState.skippedStepIds.length}
                      </span> skipped
                    </div>
                    <div>
                      <span className="text-red-600 font-medium">
                        {routineState.failedStepIds.length}
                      </span> failed
                    </div>
                  </div>
                  
                  {routineState.userFeedback && (
                    <div className="mt-2 text-xs italic text-gray-600">
                      {routineState.userFeedback}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Available Routines */}
      <div className="mb-4">
        <h4 className="text-sm font-medium mb-2">
          Available Routines ({routines.filter(r => r.enabled).length}/{routines.length})
        </h4>
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {routines.map(routine => (
            <div key={routine.id} className={`border rounded p-2 text-sm flex justify-between items-center
              ${!routine.enabled ? 'opacity-50' : ''}`}>
              <div>
                <div className="font-medium">{routine.name}</div>
                <div className="text-xs text-gray-500">
                  {routine.defaultStartTime.hour.toString().padStart(2, '0')}:
                  {routine.defaultStartTime.minute.toString().padStart(2, '0')} | 
                  {routine.daysOfWeek.join(', ')}
                </div>
              </div>
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-1 ${
                  routine.enabled ? 'bg-green-500' : 'bg-gray-300'
                }`}></div>
                <span className="text-xs">
                  {routine.enabled ? 'Enabled' : 'Disabled'}
                </span>
                
                {activeRoutines.some(rs => rs.routineId === routine.id) && (
                  <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                    Running
                  </span>
                )}
              </div>
            </div>
          ))}
          
          {routines.length === 0 && (
            <div className="text-sm text-gray-500 p-2 text-center">
              No routines configured
            </div>
          )}
        </div>
      </div>
      
      {/* Execution Log */}
      {executionLog.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">Execution Log</h4>
          <div className="text-xs space-y-1 max-h-40 overflow-y-auto">
            {executionLog.slice().reverse().map((entry, index) => (
              <div key={index} className="flex justify-between">
                <span className={`${
                  entry.type === 'ERROR' ? 'text-red-600' : 
                  entry.type === 'WARNING' ? 'text-yellow-600' : 
                  entry.type === 'SUCCESS' ? 'text-green-600' : 
                  'text-gray-600'
                }`}>
                  {entry.message}
                </span>
                <span className="text-gray-500 ml-2 shrink-0">
                  {entry.timestamp.toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MorningRoutineOrchestrator; 